/* useFlow — owns the survey's state (answers + step index) and exposes every
   read + mutation + navigation the UI needs. This is the logic that used to
   live inline in SurveyApp; pulling it into a hook makes the component thin
   and the behavior testable. Provided app-wide via SurveyContext. */
import { useState } from 'react';
import { Answers, CATEGORIES, GENDERS, GROUPS, Job, blankAnswers } from '../data';
import {
  Step,
  buildSteps,
  maxKids,
  offers as offersFor,
  score as scoreFor,
  validate,
} from '../flow';

export interface Flow {
  // state + derived
  answers: Answers;
  steps: Step[];
  idx: number;
  highWater: number;
  current: Step;
  score: number;
  offers: number;
  canAdvance: boolean;
  isPage: boolean;
  canGoBack: boolean;
  fixingGender: boolean;
  // mutations
  setA: (patch: Partial<Answers>) => void;
  setHabit: (id: string, val: number) => void;
  selectChoice: (field: keyof Answers, value: string) => void;
  selectGroup: (id: string) => void;
  selectSession: (id: string) => void;
  setGpa: (val: number) => void;
  setKids: (n: number) => void;
  setAge: (i: number, val: number) => void;
  togglePet: (t: string) => void;
  noPets: () => void;
  toggleCat: (id: string) => void;
  // navigation
  doNext: () => void | Promise<void>;
  back: () => void;
  goTo: (i: number) => void;
  goToChapter: (ch: string) => void;
  selectIndex: (i: number) => void;
  fixGender: () => void;
}

export function useFlow(): Flow {
  const [answers, setAnswers] = useState<Answers>(blankAnswers);
  const [index, setIndex] = useState(0);
  // Progress high-water mark tracked by step KEY, not index: the step list
  // grows/shrinks with answers (the conditional kidsAges step), so a stored
  // index would drift and point at a different question after a change.
  const [highWaterKey, setHighWaterKey] = useState('welcome');
  // Set when the submit bounced for a missing gender: the student is sent to
  // the identity step to pick one, with every other answer locked, and
  // continue returns them straight to the career pick.
  const [fixingGender, setFixingGender] = useState(false);

  // Derived state (steps depend on answers — conditional kid-ages step).
  const steps = buildSteps(answers);

  // Clamp index to valid range in case answers changed and steps got shorter. (Protecting against not choosing a kid)
  const idx = Math.min(index, steps.length - 1);
  const current = steps[idx];
  // Derive the high-water index from its key. If that step no longer exists
  // (kidsAges after kids went back to 0), fall back to where the user stands —
  // conservative, and advancing raises it again.
  const hwFound = steps.findIndex((s) => s.key === highWaterKey);
  const highWater = hwFound >= 0 ? Math.max(hwFound, idx) : idx;
  const score = scoreFor(answers);
  const offers = offersFor(answers);
  const canAdvance = validate(current, answers);
  const isPage =
    current.kind === 'score' || current.kind === 'careerPick' || current.kind === 'review';
  const canGoBack = !fixingGender && idx > 0 && current.kind !== 'score' && current.kind !== 'careerPick' && current.kind !== 'review';

  // ---- mutations --------------------------------------------------------
  const setA = (patch: Partial<Answers>) => setAnswers((a) => ({ ...a, ...patch }));
  const setHabit = (id: string, val: number) =>
    setAnswers((a) => ({ ...a, habits: { ...a.habits, [id]: val } }));
  const selectChoice = (field: keyof Answers, value: string) =>
    setAnswers((a) => ({ ...a, [field]: value }));
  // Picking a group derives the session when the group attends exactly one;
  // otherwise the session is cleared so the conditional session step appears.
  const selectGroup = (id: string) =>
    setAnswers((a) => {
      const g = GROUPS.find((x) => x.id === id);
      const sessionId = g && g.sessions.length === 1 ? g.sessions[0].id : '';
      return { ...a, teacherId: id, sessionId };
    });
  const selectSession = (id: string) => setAnswers((a) => ({ ...a, sessionId: id }));
  const setGpa = (val: number) => setA({ gpa: Math.round(val * 100) / 100 });
  const setKids = (n: number) =>
    setAnswers((a) => {
      const ages: number[] = [];
      for (let i = 0; i < n; i++) ages.push(typeof a.kidsAges[i] === 'number' ? a.kidsAges[i] : 2);
      return { ...a, kidsCount: n, kidsAges: ages };
    });
  const setAge = (i: number, val: number) =>
    setAnswers((a) => {
      const ages = a.kidsAges.slice();
      ages[i] = val;
      return { ...a, kidsAges: ages };
    });
  const togglePet = (t: string) =>
    setAnswers((a) => {
      const pets = a.pets.slice();
      const i = pets.indexOf(t);
      if (i >= 0) pets.splice(i, 1);
      else pets.push(t);
      return { ...a, pets, _petsAnswered: true };
    });
  const noPets = () => setAnswers((a) => ({ ...a, pets: [], _petsAnswered: true }));
  const jobInCat = (career: Job, catId: string) => {
    const c = CATEGORIES.find((x) => x.id === catId);
    return !!c && c.jobs.some((j) => j.title === career.title);
  };
  const toggleCat = (id: string) =>
    setAnswers((a) => {
      const arr = (a.careerCategories || []).slice();
      const i = arr.indexOf(id);
      let career = a.career;
      if (i >= 0) {
        arr.splice(i, 1);
        if (career && jobInCat(career, id)) career = null;
      } else {
        arr.push(id);
      }
      return { ...a, careerCategories: arr, career };
    });

  // ---- navigation -------------------------------------------------------
  // doNext Fires when the user clicks the "next" button. It advances to the next step if possible.
  // Leaving the gpa step first checks the server for a duplicate submission
  // (same event/name/gpa/session); a true duplicate sends the whole browser
  // away from the SPA instead of advancing. Network errors fail open so a
  // flaky connection never blocks a legitimate student here — the final
  // /lifestyle-next submit is the real, authoritative gate.
  const advance = () => {
    if (fixingGender && current.kind === 'identity') {
      if (!canAdvance) return;
      setFixingGender(false);
      setIndex(steps.findIndex((s) => s.kind === 'careerPick'));
      return;
    }
    if (canAdvance && idx < steps.length - 1) {
      const next = idx + 1;
      setIndex(next);
      if (next > highWater) setHighWaterKey(steps[next].key);
    }
  };
  const doNext = async () => {
    if (current.kind === 'review') return;
    if (current.kind === 'scale' && current.key === 'gpa') {
      if (!canAdvance) return;
      try {
        const csrf = document.querySelector('meta[name="_csrf"]')?.getAttribute('content') || '';
        const resp = await fetch('/survey/check-duplicate', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'x-csrf-token': csrf,
          },
          body: new URLSearchParams({
            data: JSON.stringify({
              eventId: answers.eventId,
              firstName: answers.firstName,
              lastName: answers.lastName,
              gpa: answers.gpa,
              sessionId: answers.sessionId,
            }),
          }),
        });
        const data = await resp.json();
        if (data.duplicate) {
          window.location.href = '/survey-already-submitted';
          return;
        }
      } catch {
        // fail open — proceed as normal
      }
      advance();
      return;
    }
    advance();
  };
  const back = () => {
    if (!fixingGender && idx > 0) setIndex(idx - 1);
  };
  // Forward jumps must never skip an unanswered question: answers can change
  // and re-open earlier steps (adding kids after pets was answered inserts an
  // unanswered kidsAges), so cap every jump at the first failing step.
  const firstInvalid = steps.findIndex((s) => !validate(s, answers));
  const reachable = firstInvalid === -1 ? steps.length - 1 : firstInvalid;
  const goTo = (i: number) => {
    if (fixingGender) return;
    setIndex(Math.max(0, Math.min(highWater, reachable, i)));
  };
  const fixGender = () => {
    setFixingGender(true);
    setIndex(steps.findIndex((s) => s.kind === 'identity'));
  };
  const goToChapter = (ch: string) => {
    const i = steps.findIndex((x) => x.chapter === ch);
    if (i >= 0 && i <= highWater) goTo(i);
  };
  const selectIndex = (i: number) => {
    switch (current.kind) {
      case 'choice':
        if (current.options && current.options[i] && current.field)
          selectChoice(current.field, current.options[i].value);
        break;
      case 'habit':
        if (current.habit && current.habit.options[i])
          setHabit(current.habit.id, i);
        break;
      case 'identity':
        if (GENDERS[i]) setA({ gender: GENDERS[i].value });
        break;
      case 'group':
        if (GROUPS[i]) selectGroup(GROUPS[i].id);
        break;
      case 'session': {
        const g = GROUPS.find((x) => x.id === answers.teacherId);
        if (g && g.sessions[i]) selectSession(g.sessions[i].id);
        break;
      }
      case 'kids':
        if (i <= maxKids(answers)) setKids(i);
        break;
      case 'pets': {
        const types = ['Dog', 'Cat', 'Bird', 'Fish', 'Reptile'];
        if (i < types.length) togglePet(types[i]);
        else if (i === types.length) noPets();
        break;
      }
    }
  };

  return {
    answers, steps, idx, highWater, current, score, offers, canAdvance, isPage, canGoBack, fixingGender,
    setA, setHabit, selectChoice, selectGroup, selectSession, setGpa, setKids, setAge, togglePet, noPets, toggleCat,
    doNext, back, goTo, goToChapter, selectIndex, fixGender,
  };
}
